/**
 * @file DocumentStatusBadge.tsx
 * @description Бейдж статуса документа — один на все документы админки
 * (phase19.24, UI-контракт §2.1; phase19.31 шаг О4 — шесть тонов кабинета).
 *
 * Домен отдаёт только тон документа из закрытого реестра, как он выглядит —
 * решает реестр кабинета `lib/shop/status-meta` (SC25, SC26): пилюля с
 * точкой; «Черновик» — серый, «В работе» — синий, «Ждёт решения» —
 * янтарный, «Недостача» — красный, «Проведено/Закрыто» — ЗЕЛЁНЫЙ (раньше —
 * цвет основной кнопки), «Отменено» — красный (раньше — бежевый контур).
 * Прогресс в бейдж не кладём: его место в плитке «Собрано».
 *
 * ```tsx
 * <DocumentStatusBadge tone="active">В работе</DocumentStatusBadge>
 * <DocumentStatusBadge tone="draft">Черновик</DocumentStatusBadge>
 * ```
 */

import type { ReactNode } from "react";

import { Badge } from "./badge";
import {
  DOCUMENT_TONE_STATUS,
  STATUS_TONE_VARIANT,
  type DocumentStatusTone,
} from "@/lib/shop/status-meta";

/**
 * Тон статуса документа:
 * - `draft` — документ не проведён, менять можно;
 * - `active` — документ живёт: новое задание, в работе, в пути;
 * - `attention` — нужен присмотр человека (ждёт решения, просрочено);
 * - `danger` — недостача, ошибка, блокировка;
 * - `done` — проведён / закрыт;
 * - `cancelled` — отменён, движений нет.
 */
export type { DocumentStatusTone };

interface Props {
  tone: DocumentStatusTone;
  /** Подпись статуса человеческим текстом: «Черновик», «В работе». */
  children: ReactNode;
  className?: string;
}

export function DocumentStatusBadge({ tone, children, className }: Props) {
  return (
    <Badge variant={STATUS_TONE_VARIANT[DOCUMENT_TONE_STATUS[tone]]} data-tone={tone} className={className}>
      {children}
    </Badge>
  );
}
